import { createFileRoute, Link } from "@tanstack/react-router"
import * as React from "react"
import {
	AttentionCount,
	closeStatusPresentation,
	ID_LINK_CLASS,
	Money,
	QueryErrorLine,
	SkeletonRows,
	StatusView,
} from "@/components/accounting/accounting-ui"
import {
	formatEur,
	monthStatusPresentation,
	periodKey,
} from "@/components/accounting/reconciliation-format"
import {
	PageHeader,
	Panel,
	Section,
	SectionStack,
	Stat,
	StatStrip,
} from "@/components/layout"
import { Button } from "@/components/ui/button"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { useMonthlyCloses } from "@/queries/marketplace/use-monthly-closes"
import { useReconciliationMonths } from "@/queries/marketplace/use-stripe-reconciliation"
import {
	useVatDeclarations,
	useVatProvisionSummary,
} from "@/queries/marketplace/use-vat-declarations"
import { useOutstandingVatSummary } from "@/queries/marketplace/use-vat-remittances"

export const Route = createFileRoute("/accounting/")({
	component: AccountingDashboardPage,
})

function ViewAllLink({ children }: { children: React.ReactNode }) {
	return (
		<Button variant="ghost" size="sm" asChild>
			{children}
		</Button>
	)
}

function ValueSkeleton() {
	return (
		<span className="inline-block h-5 w-28 animate-pulse rounded-md bg-accent align-middle" />
	)
}

function AccountingDashboardPage() {
	const closes = useMonthlyCloses({})
	const outstanding = useOutstandingVatSummary()
	const reconciliation = useReconciliationMonths("EU")
	const vatDeclarations = useVatDeclarations({})
	const vatProvisions = useVatProvisionSummary()

	const vatToPay = React.useMemo(() => {
		const items = vatDeclarations.data?.items ?? []
		const payable = items.filter(
			(d) => d.status !== "provisioned" && d.status !== "superseded",
		)
		const payableOutstandingEur = payable.reduce(
			(sum, d) => sum + d.outstandingEur,
			0,
		)
		const nextDue = payable
			.filter((d) => d.outstandingEur > 0)
			.sort(
				(a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
			)[0]
		const provisionedEur =
			vatProvisions.data?.schemes.reduce(
				(sum, s) => sum + s.cumulativeEur,
				0,
			) ?? 0
		return { payableOutstandingEur, nextDue, provisionedEur }
	}, [vatDeclarations.data, vatProvisions.data])

	const vatLoading = vatDeclarations.isLoading || vatProvisions.isLoading

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Accounting"
				description="Monthly closes, VAT remittances and the Stripe reconciliation ledger."
			/>

			<SectionStack>
				<Section
					label="VAT to pay"
					action={
						<ViewAllLink>
							<Link to="/accounting/vat-declarations">View all</Link>
						</ViewAllLink>
					}
				>
					<StatStrip>
						<Stat
							label="Payable"
							value={
								vatLoading ? (
									<ValueSkeleton />
								) : (
									formatEur(vatToPay.payableOutstandingEur)
								)
							}
							muted={!vatLoading && vatToPay.payableOutstandingEur === 0}
							detail="French return, EU OSS, IOSS"
						/>
						<Stat
							label="Provisioned"
							value={
								vatLoading ? (
									<ValueSkeleton />
								) : (
									formatEur(vatToPay.provisionedEur)
								)
							}
							muted
							detail="UK VAT, Swiss VAT: registration pending, not payable yet"
						/>
						<Stat
							label="Next due"
							value={
								vatLoading ? (
									<ValueSkeleton />
								) : vatToPay.nextDue ? (
									new Date(vatToPay.nextDue.dueDate).toLocaleDateString()
								) : (
									"–"
								)
							}
							muted={!vatLoading && !vatToPay.nextDue}
							detail={
								vatToPay.nextDue ? (
									<Link
										to="/accounting/vat-declarations/$declarationId"
										params={{ declarationId: vatToPay.nextDue.declarationId }}
										className={ID_LINK_CLASS}
									>
										{vatToPay.nextDue.scheme} {vatToPay.nextDue.filingPeriod}
									</Link>
								) : undefined
							}
						/>
					</StatStrip>
				</Section>

				<Section
					label="Stripe reconciliation (EU)"
					action={
						<ViewAllLink>
							<Link to="/accounting/reconciliation">View all</Link>
						</ViewAllLink>
					}
				>
					{reconciliation.isError ? (
						<QueryErrorLine
							what="the reconciliation months"
							error={reconciliation.error}
						/>
					) : (
						<Panel>
							{reconciliation.isLoading ? (
								<SkeletonRows rows={4} />
							) : (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Month</TableHead>
											<TableHead>Status</TableHead>
											<TableHead className="text-right">
												Balance transactions
											</TableHead>
											<TableHead className="text-right">
												Open exceptions
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{(reconciliation.data?.months ?? [])
											.slice(0, 6)
											.map((month) => (
												<TableRow key={month.period}>
													<TableCell>
														<Link
															to="/accounting/reconciliation/$region/$period"
															params={{
																region: month.region,
																period: month.period,
															}}
															className={ID_LINK_CLASS}
														>
															{month.period}
														</Link>
													</TableCell>
													<TableCell>
														<StatusView
															presentation={monthStatusPresentation(
																month.status,
															)}
														/>
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{month.balanceTransactions}
													</TableCell>
													<TableCell className="text-right">
														<AttentionCount
															count={
																month.openStripeExceptions +
																month.openDbExceptions
															}
														/>
													</TableCell>
												</TableRow>
											))}
									</TableBody>
								</Table>
							)}
						</Panel>
					)}
				</Section>

				<div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
					<Section
						label="Outstanding VAT to remit"
						action={
							<ViewAllLink>
								<Link to="/accounting/vat-remittances">View all</Link>
							</ViewAllLink>
						}
					>
						{outstanding.isError ? (
							<QueryErrorLine
								what="outstanding VAT"
								error={outstanding.error}
							/>
						) : (
							<Panel>
								{outstanding.isLoading ? (
									<SkeletonRows rows={4} />
								) : (
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Scheme</TableHead>
												<TableHead className="text-right">Collected</TableHead>
												<TableHead className="text-right">Remitted</TableHead>
												<TableHead className="text-right">
													Outstanding
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{outstanding.data?.perScheme.map((row) => (
												<TableRow key={row.scheme}>
													<TableCell className="font-mono">
														{row.scheme}
													</TableCell>
													<TableCell className="text-right">
														<Money cents={row.collectedEur} currency="EUR" />
													</TableCell>
													<TableCell className="text-right text-muted-foreground">
														<Money cents={row.remittedEur} currency="EUR" />
													</TableCell>
													<TableCell className="text-right font-medium">
														<Money
															cents={row.outstandingEur}
															currency="EUR"
															muteZero
														/>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</Panel>
						)}
					</Section>

					<Section
						label="Latest closes"
						action={
							<ViewAllLink>
								<Link
									to="/accounting/monthly-closes"
									search={{
										region: undefined,
										currency: undefined,
										year: undefined,
										status: undefined,
									}}
								>
									View all
								</Link>
							</ViewAllLink>
						}
					>
						{closes.isError ? (
							<QueryErrorLine what="closes" error={closes.error} />
						) : (
							<Panel>
								{closes.isLoading ? (
									<SkeletonRows rows={4} />
								) : (
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Period</TableHead>
												<TableHead>Region</TableHead>
												<TableHead>Status</TableHead>
												<TableHead
													className="text-right"
													title="Gross GMV − Refunded GMV (in-period). Cancelled orders aren't in Gross."
												>
													Net GMV
												</TableHead>
												<TableHead className="text-right">
													Net revenue
												</TableHead>
												<TableHead
													className="text-right"
													title="Retained buyer-paid shipping − carrier label cost, on managed (SendCloud) orders."
												>
													Shipping margin
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{(closes.data?.items ?? []).slice(0, 8).map((c) => (
												<TableRow key={c.closeId}>
													<TableCell>
														<span className="font-mono">
															{periodKey(c.year, c.month)}
														</span>
													</TableCell>
													<TableCell>{c.region}</TableCell>
													<TableCell>
														<StatusView
															presentation={closeStatusPresentation(c.status)}
														/>
													</TableCell>
													<TableCell className="text-right font-medium">
														<Money cents={c.netGmvEur} currency="EUR" />
													</TableCell>
													<TableCell className="text-right">
														<Money
															cents={c.netPlatformRevenueEur}
															currency="EUR"
														/>
													</TableCell>
													<TableCell className="text-right">
														<Money cents={c.shippingMarginEur} currency="EUR" />
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</Panel>
						)}
					</Section>
				</div>
			</SectionStack>
		</div>
	)
}
